export type QrCodeScannedCallback = (payload: string) => void;
export type ScannerHandle = { stop: () => void };

declare class BarcodeDetector {
	constructor(options?: { formats?: string[] });

	detect(video: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
}

/**
 * Uses the native BarcodeDetector API to scan QR codes from a video stream.
 * Needs to be initialized with a container HTMLElement containing a <video> element with an id matching the videoSelector and a callback function
 * The video element is used to display the camera stream and the BarcodeDetector API is used to detect QR codes in the video stream.
 * The scanner can be started and stopped using the start() and stop() methods and will automatically be stopped when a QR code is detected
 * and the callback function is called with the scanned QR code as a parameter.
 */
export class QrCodeScanner {
	private readonly video: HTMLVideoElement;
	private readonly barcodeDetector: BarcodeDetector;
	private scanning: boolean = false;
	private readonly onScannedCallback: QrCodeScannedCallback;

	constructor(
		container: HTMLElement,
		videoSelector: string,
		onScannedCallback: QrCodeScannedCallback
	) {
		const el = container.querySelector(videoSelector);
		if (!(el instanceof HTMLVideoElement)) {
			throw new Error(`Element with selector "${videoSelector}" is not a <video> element.`);
		}
		if (!('BarcodeDetector' in window)) {
			throw new Error('BarcodeDetector is not supported in this browser.');
		}

		this.video = el;
		this.onScannedCallback = onScannedCallback;
		this.barcodeDetector = new BarcodeDetector({formats: ['qr_code']});
	}

	public async start(): Promise<ScannerHandle> {
		try {
			this.video.srcObject = await navigator.mediaDevices.getUserMedia({
				video: {facingMode: 'environment'}
			});
			this.video.style.display = 'block';

			this.video.onloadedmetadata = () => {
				void this.video.play();
				this.scanning = true;
				this.detectLoop();
			};

			return {
				stop: () => this.stop()
			};
		} catch (err) {
			console.error('Camera access error:', err);
			throw err;
		}
	}

	private detectLoop() {
		if (!this.scanning) return;

		this.barcodeDetector.detect(this.video)
			.then(barcodes => {
				if (barcodes.length > 0) {
					const result = barcodes[0].rawValue;
					this.scanning = false;
					this.stop();
					this.onScannedCallback?.(result);
				} else {
					requestAnimationFrame(() => this.detectLoop());
				}
			})
			.catch(() => this.detectLoop())
	}

	public stop() {
		const stream = this.video.srcObject as MediaStream | null;
		if (stream) {
			stream.getTracks().forEach(track => track.stop());
			this.video.srcObject = null;
		}
		this.scanning = false;
	}
}

